<script setup lang="ts">
  import { ref, watch } from 'vue';
  import { CustomersService } from '@/api/services/customers.service';
  import ModalsCustomer from '@/components/modals/customer/ModalsCustomer.vue';
  import ModalsCustomerBalance from '@/components/modals/customer/ModalsCustomerBalance.vue';
  import { useModal } from '@/composition/use-modal';
  import { useUi } from '@/composition/use-ui';
  import { ICustomer } from '@/interfaces/ICustomer';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import { IDataOptions } from '@/interfaces/IDataOptions';
  import { debounce } from '@/utils';

  const restaurantStore = useRestaurantStore();
  const ui = useUi();
  const modals = useModal();

  const customers = ref<ICustomer[] | null>(null);

  const paginationData = ref<IDataOptions>({
    page: 1,
    itemsPerPage: 10,
    mustSort: true,
    sortBy: ['name'],
    sortDesc: [false],
    search: '',
  });

  const totalItems = ref(0);

  const loading = ref(false);
  async function loadCustomers() {
    if (!restaurantStore.restaurant.id) return;

    try {
      loading.value = true;

      const response = await CustomersService.getAll({
        restaurantId: restaurantStore.restaurant.id,
        options: paginationData.value,
      });

      customers.value = response.items;
      totalItems.value = response.total;
    } catch (error) {
      ui.serverError(error, 'Error al cargar los clientes');
    } finally {
      loading.value = false;
    }
  }

  const debouncedLoadCustomers = debounce(loadCustomers, 500);

  watch(
    [() => restaurantStore.restaurant.id],
    () => {
      loadCustomers();
    },
    { immediate: true }
  );

  watch(
    paginationData,
    () => {
      debouncedLoadCustomers();
    },
    { deep: true }
  );

  const headers = [
    { text: 'N°', value: 'nativeId' },
    { text: 'Nombre', value: 'name' },
    // { text: 'Identificador', value: 'customId' },
    // { text: 'Tipo doc.', value: 'taxIdType' },
    // { text: 'Nro. doc.', value: 'taxId' },
    { text: 'Saldo', value: 'balance' },
    { text: 'Teléfono', value: 'tel' },
    { text: 'Correo', value: 'email' },
    { text: 'Dirección', value: 'address' },
    { text: 'Acciones', value: 'actions', sortable: false },
  ];

  function formatBalance(balance: number) {
    return `$${balance.toFixed(2)}`;
  }

  function openNewCustomerDialog() {
    modals.show(
      ModalsCustomer,
      {},
      {
        name: 'customer',
        adaptive: true,
        classes: 'dialog--height-limit',
        height: 'auto',
        scrollable: true,
        maxWidth: window.innerWidth * 0.95,
        width: 600,
        clickToClose: false,
      },
      {
        saved: () => {
          loadCustomers();
        },
      }
    );
  }

  function openEditCustomerDialog(customer: ICustomer) {
    modals.show(
      ModalsCustomer,
      { customerToEdit: customer },
      {
        name: 'customer',
        adaptive: true,
        classes: 'dialog--height-limit',
        height: 'auto',
        scrollable: true,
        maxWidth: window.innerWidth * 0.95,
        width: 600,
        clickToClose: false,
      },
      {
        saved: () => {
          loadCustomers();
        },
      }
    );
  }

  function openBalanceDialog(customer: ICustomer) {
    modals.show(
      ModalsCustomerBalance,
      { customer },
      {
        name: 'customer-balance',
        adaptive: true,
        classes: 'dialog--height-limit',
        height: 'auto',
        scrollable: true,
        maxWidth: window.innerWidth * 0.95,
        width: 600,
        clickToClose: false,
      },
      {
        saved: () => {
          loadCustomers();
        },
      }
    );
  }

  const deletingCustomerId = ref<number | null>(null);
  async function deleteCustomer(customer: ICustomer) {
    if (deletingCustomerId.value) return;

    const confirmed = await modals.confirm({
      title: 'Eliminar cliente',
      message: '¿Está seguro de que desea eliminar este cliente?',
      acceptText: 'Eliminar',
      cancelText: 'Cancelar',
      acceptColor: 'error',
    });

    if (!confirmed) return;

    try {
      deletingCustomerId.value = customer.id;
      await CustomersService.delete(customer.id);
      loadCustomers();
    } catch (error) {
      ui.serverError(error, 'Error al eliminar el cliente');
    } finally {
      deletingCustomerId.value = null;
    }
  }
</script>

<template>
  <div class="customers-page page-container">
    <div class="page-inner">
      <v-card class="elevation-0">
        <v-card-title>
          <div class="header-container">
            <div class="header-title-container">
              <v-icon>mdi-account-group</v-icon>
              <span>Clientes</span>
            </div>
            <div class="header-actions-container">
              <v-btn color="success" @click="openNewCustomerDialog">
                <v-icon left>mdi-plus</v-icon>
                <span>Nuevo</span>
              </v-btn>
            </div>
          </div>
        </v-card-title>
        <v-card-subtitle>
          Administre sus clientes y sus saldos desde aquí
        </v-card-subtitle>
        <v-card-text>
          <div class="customers-list">
            <div class="search-container">
              <v-text-field
                v-model="paginationData.search"
                prepend-inner-icon="mdi-magnify"
                label="Buscar cliente"
                clearable
                hide-details
                outlined
                dense
              />
            </div>

            <template v-if="!customers">
              <v-skeleton-loader type="table-row" />
              <v-skeleton-loader type="table-row" />
              <v-skeleton-loader type="table-row" />
            </template>
            <template v-else-if="customers.length === 0">
              <p class="tw-text-center tw-text-gray-500">
                No hay clientes registrados
              </p>
            </template>
            <template v-else>
              <v-data-table
                dense
                :items="customers"
                :headers="headers"
                :options.sync="paginationData"
                :server-items-length="totalItems"
                :footer-props="{
                  'items-per-page-options': [10, 20, 50, 100, 200],
                }"
                :loading="loading"
              >
                <template v-slot:item.balance="{ item }">
                  {{ formatBalance(item.balance) }}
                </template>
                <template v-slot:item.tel="{ item }">
                  {{ item.tel || '-' }}
                </template>
                <template v-slot:item.email="{ item }">
                  {{ item.email || '-' }}
                </template>
                <template v-slot:item.actions="{ item }">
                  <div class="actions">
                    <v-btn
                      x-small
                      depressed
                      color="primary"
                      @click="openBalanceDialog(item)"
                    >
                      <v-icon left small>mdi-cash</v-icon>
                      <span>Saldo</span>
                    </v-btn>
                    <v-btn icon small @click="openEditCustomerDialog(item)">
                      <v-icon small>mdi-pencil</v-icon>
                    </v-btn>
                    <v-btn
                      icon
                      small
                      @click="deleteCustomer(item)"
                      :loading="deletingCustomerId === item.id"
                      :disabled="!!deletingCustomerId"
                    >
                      <v-icon small>mdi-delete</v-icon>
                    </v-btn>
                  </div>
                </template>
              </v-data-table>
            </template>
          </div>
        </v-card-text>
      </v-card>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .page-container {
    @apply tw-flex tw-justify-center tw-p-4;

    .page-inner {
      @apply tw-w-full tw-max-w-6xl;

      .header-container {
        @apply tw-flex tw-justify-between tw-items-center tw-w-full;

        .header-title-container {
          @apply tw-flex tw-items-center tw-space-x-1.5;
        }
      }

      .customers-list {
        @apply tw-space-y-4;

        .search-container {
          @apply tw-mb-4;
        }

        .actions {
          @apply tw-space-x-2;
        }
      }
    }
  }
</style>
